/* global SideDrawer, debounce, initLazyImages */

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.historyChangeHandler);

      if (this.breakpointChangeHandler) {
        window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');

      if (this.filteringEnabled) {
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = this.querySelector('.facets__active-filters');
        this.activeFiltersList = this.querySelector('.active-filters');
        this.activeFiltersHeader = this.querySelector('.active-filters-header');
        this.footer = this.querySelector('.facets__footer');
      }

      if (this.sortingEnabled) {
        this.mobileSortByOptions = this.querySelectorAll('.js-drawer-sort-by');
        this.desktopSortBy = document.querySelector('.products-toolbar__sort');
      }

      this.handleBreakpointChange();
      this.addListeners();
      this.updateNumericRangeFilters(window.location.search)
    }

    addListeners() {
      if (this.filteringEnabled) {
        this.breakpointChangeHandler = this.breakpointChangeHandler
          || this.handleBreakpointChange.bind(this);
        this.filters.addEventListener('click', this.handleFiltersClick.bind(this));
        this.filters.addEventListener('input', debounce(this.handleFilterChange.bind(this), 500));
        this.activeFilters.addEventListener('click', this.handleActiveFiltersClick.bind(this));
        window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }

      if (this.sortingEnabled) {
        this.desktopSortBy.addEventListener('change', this.handleFilterChange.bind(this));
      }

      this.historyChangeHandler = this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.addEventListener('popstate', this.historyChangeHandler);
    }

    /**
     * Handles viewport breakpoint changes.
     */
    handleBreakpointChange() {
      if (theme.mediaMatches.lg) {
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('aria-modal');
        this.removeAttribute('role');
      } else {
        this.close();
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-hidden', 'true');
        this.hidden = false;
      }
    }

    /**
     * Handles 'input' events on the filters and 'change' events on the sort by dropdown.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      const formData = new FormData(this.form);
      const searchParams = new URLSearchParams(formData);
      const emptyParams = [];

      if (this.sortingEnabled) {
        let currentSortBy = searchParams.get('sort_by');

        // Keep the mobile facets form sync'd with the desktop sort by dropdown
        if (evt.target.tagName === 'CUSTOM-SELECT') {
          this.mobileSortByOptions.forEach((option) => {
            option.checked = option.value === evt.detail.selectedValue;
            currentSortBy = evt.detail.selectedValue;
          });
        }

        // Set the 'sort_by' parameter.
        searchParams.set('sort_by', currentSortBy);
      }

      // Get empty parameters.
      searchParams.forEach((value, key) => {
        if (!value) emptyParams.push(key);
      });

      // Remove empty parameters.
      emptyParams.forEach((key) => {
        searchParams.delete(key);
      });

      this.applyFilters(searchParams.toString(), evt);
    }

    /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
    handleFiltersClick(evt) {
      const { target } = evt;

      // Filter 'clear' button clicked.
      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
      }

      // Filter 'show more' button clicked.
      if (target.matches('.js-show-more')) {
        const filter = target.closest('.filter');
        target.remove();

        filter.querySelectorAll('li').forEach((el) => {
          el.classList.remove('js-hidden');
        });
      }
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      if (evt.target.tagName !== 'A') return;
      evt.preventDefault();
      this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
    }

    /**
     * Handles history changes (e.g. back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = '';

        if (evt.state && evt.state.searchParams) {
          ({ searchParams } = evt.state);
        }

        this.applyFilters(searchParams, null, false);
      }
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {object} evt - Event object.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     */
    async applyFilters(searchParams, evt, updateUrl = true) {
      const customPagination = document.querySelector('custom-pagination');
      if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'true';

      this.results.classList.add('is-loading');

      // Disable "Show X results" button until submission is complete.
      const closeBtn = this.querySelector('.js-close-drawer-mob');
      closeBtn.ariaDisabled = 'true';
      closeBtn.classList.add('is-loading');

      // Fetch filtered products markup.
      const response = await fetch(`${window.location.pathname}?${searchParams}`);

      if (response.ok) {
        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        // Update the filters.
        if (this.filteringEnabled) this.updateFilters(tmpl.content, evt, searchParams);

        // Update the label of the mobile filter button
        closeBtn.innerText = tmpl.content.querySelector('.js-close-drawer-mob').innerText;

        // Update the results.
        this.results.innerHTML = tmpl.content.getElementById('filter-results').innerHTML;

        // Reinitialize lazyload images after filters applied.
        if (typeof initLazyImages === 'function') initLazyImages();

        // Reinitialize any custom pagination
        if (customPagination && customPagination.reload) customPagination.reload();

        // Update the URL.
        if (updateUrl) FacetFilters.updateURL(searchParams);

        // Scroll to the top of the results if needed
        if (this.results.getBoundingClientRect().top < 0) {
          // If the header is sticky, compensate for it when scrolling to elements
          let headerHeight = 0;
          if (document.querySelector('store-header[data-is-sticky="true"]')) {
            headerHeight = Number.parseInt(
              getComputedStyle(this.parentElement)
                .getPropertyValue('--header-height')
                .replace('px', ''),
              10
            ) || 0;
          }
          window.scrollTo({
            top: this.results.getBoundingClientRect().top + window.scrollY - headerHeight - 45,
            behavior: 'smooth'
          });
        }

        // Enable the "Show X results" button
        closeBtn.classList.remove('is-loading');
        closeBtn.removeAttribute('aria-disabled');

        // Renable infinite scroll
        if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';

        // Broadcast the update for anything else to hook into
        document.dispatchEvent(new CustomEvent('on:facet-filters:updated'), { bubbles: true });
      }

      this.results.classList.remove('is-loading');
    }

    /**
     * Updates the filters with the fetched data.
     * @param {string} html - HTML of the fetched document.
     * @param {object} evt - Event object.
     */
    updateFilters(html, evt, searchParams) {
      document.querySelectorAll('.filter').forEach((filter) => {
        const { index } = filter.dataset;
        const fetchedFilter = html.querySelector(`.filter[data-index="${index}"]`);

        if (filter.dataset.type !== 'sort') {
          if (filter.dataset.type === 'price_range') {
            FacetFilters.updateFilter(filter, fetchedFilter, false);

            if (!evt || evt.target.tagName !== 'INPUT') {
              filter.querySelectorAll('input').forEach((input) => {
                input.value = html.getElementById(input.id).value;
              });
            }
          } else if (evt && evt.target.tagName === 'INPUT') {
            const changedFilter = evt.target.closest('.filter');

            if (changedFilter && changedFilter.dataset) {
              FacetFilters.updateFilter(
                filter,
                fetchedFilter,
                filter.dataset.index !== changedFilter.dataset.index
              );
            }
          } else {
            FacetFilters.updateFilter(filter, fetchedFilter, true);
          }
        }
      });

      // Update active filters.
      this.updateActiveFilters(html);

      this.updateNumericRangeFilters(searchParams)

      // Update '[x] results' button (mobile only).
      const footerEl = html.querySelector('.facets__footer');
      this.footer.innerHTML = footerEl.innerHTML;
    }

    /**
     * Updates a filter.
     * @param {Element} filter - Filter element.
     * @param {Element} fetchedFilter - Fetched filter element.
     * @param {boolean} updateAll - Update all filter markup or just toggle/header.
     */
    static updateFilter(filter, fetchedFilter, updateAll) {
      if (fetchedFilter) {
        filter.hidden = false;
        if (updateAll) {
          filter.innerHTML = fetchedFilter.innerHTML;
        } else {
          // Update toggle and header only.
          filter.replaceChild(
            fetchedFilter.querySelector('.filter__toggle'),
            filter.querySelector('.filter__toggle')
          );
          filter.querySelector('.filter__header').innerHTML = fetchedFilter.querySelector('.filter__header').innerHTML;
        }
      } else {
        filter.hidden = true;
      }
    }

    /**
     * Updates the active filters.
     * @param {string} html - HTML of the fetched page.
     */
    updateActiveFilters(html) {
      const activeFiltersList = html.querySelector('.active-filters');
      const activeFiltersHeader = html.querySelector('.active-filters-header');

      this.activeFiltersList.innerHTML = activeFiltersList.innerHTML;
      this.activeFiltersHeader.innerHTML = activeFiltersHeader.innerHTML;
      this.activeFilters.hidden = !this.activeFiltersList.querySelector('.active-filter');

    }

    updateNumericRangeFilters(searchParams) {
      // Copy any window.location.search params that start with nr_min or nr_max to active filter urls
      const urlSearchParams = new URLSearchParams(searchParams);
      const activeFilters = this.activeFiltersList.querySelectorAll('.active-filter');
      activeFilters.forEach(activeFilter => {
        const href = activeFilter.href
        const url = new URL(href);
        urlSearchParams.forEach((value, key) => {
          if (key.startsWith('nr_min') || key.startsWith('nr_max')) {
            url.searchParams.set(key, value);
          }
        });
        activeFilter.href = url.toString();
      });

      //Group the numeric range filters by filterParamName
      const numericRangeFilterParams = Array.from(new Set(Array.from(urlSearchParams.keys()).filter(key => key.startsWith('nr_min') || key.startsWith('nr_max')).map(key => key.split('_')[2])));
      numericRangeFilterParams.forEach(filterParamName => {
        const filterUnit = document.getElementById(`numeric-range-${filterParamName}`).dataset.filterUnit;
        const minValue = urlSearchParams.get(`nr_min_${filterParamName}`);
        const maxValue = urlSearchParams.get(`nr_max_${filterParamName}`);
        const activeFilter = document.createElement('a');
        // Set the href to the current url with the filterParamName removed
        const updatedSearchParams = new URLSearchParams(searchParams);
        const keysToDelete = [];
        updatedSearchParams.forEach((value, key) => {
          if (key.startsWith(`nr_min_${filterParamName}`) || key.startsWith(`nr_max_${filterParamName}`) || key === filterParamName) {
            keysToDelete.push(key);
          }
        });

        // Iterate over the keys to delete and remove them from updatedSearchParams
        keysToDelete.forEach(key => {
          updatedSearchParams.delete(key);
        });
        activeFilter.href = `${window.location.pathname}?${updatedSearchParams.toString()}`;
        activeFilter.classList.add('active-filter', 'flex', 'items-center', 'text-current');
        activeFilter.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5" fill="none" fill-rule="evenodd" stroke-linejoin="round" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="M5 19 19 5M5 5l14 14"></path></svg>${minValue} ${filterUnit} - ${maxValue} ${filterUnit}`;
        this.activeFiltersList.appendChild(activeFilter);
      })
      this.activeFilters.hidden = !this.activeFiltersList.querySelector('.active-filter');

    }

    /**
     * Updates the url with the current filter/sort parameters.
     * @param {string} searchParams - Filter/sort parameters.
     */
    static updateURL(searchParams) {
      window.history.pushState(
        { searchParams },
        '',
        `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
      );
    }
  }

  customElements.define('facet-filters', FacetFilters);
}

if (!customElements.get('numeric-range')) {
  class NumericRange extends HTMLElement {
    constructor() {
      super();
      window.initLazyScript(this, this.init.bind(this));
    }

    init() {
      const filterParamName = this.dataset.filterParamName;
      const rawValues = JSON.parse(this.querySelector('script').innerText);
      this.filterParamName = filterParamName;
      this.filterUnit = this.dataset.filterUnit;
      const unitRegexMap = {
        '"': /\((\d+([.,]\d+)?)"\)/,
        'cd/m²': /(\d+([.,]\d+)?)\s*cd\/m²/,
        'mm': /(\d+([.,]\d+)?)\s*mm/,
        'kg': /(\d+([.,]\d+)?)\s*(kg|g)/
      };
            
      const regex = unitRegexMap[this.filterUnit];
      
      this.mappedValues = rawValues.map(v => {
        let parsedValue = 0;
      
        if (regex) {
          const match = v.label.match(regex);
          parsedValue = match ? parseFloat(match[1]) : 0;          
          // Special case, if match[2] is 'g' then we need to convert to kg
          if (match && match[2] === 'g') {
            parsedValue = parsedValue / 1000;
          }
        }
      
        return {
          parsedValue: parsedValue,
          value: htmlDecode(v.value),
          label: v.label
        };
      });
      this.numericValues = this.mappedValues.map(value => value.parsedValue);
      
      const minValue = Math.min(...this.numericValues);
      const maxValue = Math.max(...this.numericValues);
    
      // Get elements
      const minNumberInput = document.getElementById(`numeric-range-number-min-${filterParamName}`);
      const maxNumberInput = document.getElementById(`numeric-range-number-max-${filterParamName}`);
      const minSliderInput = document.getElementById(`numeric-range-slider-min-${filterParamName}`);
      const maxSliderInput = document.getElementById(`numeric-range-slider-max-${filterParamName}`);
      const hiddenMinInput = this.querySelector(`[name="nr_min_${filterParamName}"]`);
      const hiddenMaxInput = this.querySelector(`[name="nr_max_${filterParamName}"]`);

      // Get the select min and max values from window.location.search 
      const urlSearchParams = new URLSearchParams(window.location.search);
      const selectedMin = urlSearchParams.get(`nr_min_${filterParamName}`);
      const selectedMax = urlSearchParams.get(`nr_max_${filterParamName}`);
   
      // Set initial values
      minNumberInput.placeholder = minValue;
      maxNumberInput.placeholder = maxValue;
      minNumberInput.min = minValue;
      maxNumberInput.max = maxValue;
      maxNumberInput.min = minValue;
      minSliderInput.min = minValue;
      maxSliderInput.min = minValue;
      minSliderInput.max = maxValue;
      maxSliderInput.max = maxValue;

      // Set initial values
      minSliderInput.value = selectedMin || minValue;
      maxSliderInput.value = selectedMax || maxValue;
      minNumberInput.value = selectedMin || minValue;
      maxNumberInput.value = selectedMax || maxValue;
      hiddenMinInput.value = selectedMin || minValue;
      hiddenMaxInput.value = selectedMax || maxValue;
    
      // Store references and values
      this.minNumberInput = minNumberInput;
      this.maxNumberInput = maxNumberInput;
      this.minSliderInput = minSliderInput;
      this.maxSliderInput = maxSliderInput;
      this.hiddenMinInput = hiddenMinInput;
      this.hiddenMaxInput = hiddenMaxInput;
      this.minValue = minValue;
      this.maxValue = maxValue;
      this.timeout = null;
    
      // Add event listener
      this.addEventListener('input', this.handleInput.bind(this));
    }
        
    /**
     * Handles 'input' events on the numeric range component.
     * @param {object} evt - Event object.
     */
    handleInput(evt) {
      if (evt.detail !== undefined) {
        clearTimeout(this.timeout);

        this.timeout = setTimeout(() => {
          this.updateSliderInputs(evt);
          this.updateNumberInputs();
          this.updateActualValues();
        }, 500);
      } else {
        this.updateSliderInputs(evt);
        this.updateNumberInputs();
        this.updateActualValues();
      }
    }

    updateActualValues() {
      //Actual values are input checkboxes, with mapped values this.mappedValues. Update each checkbox based on the slider values
      const minInputValue = parseFloat(this.minNumberInput.value) || this.minValue;
      const maxInputValue = parseFloat(this.maxNumberInput.value) || this.maxValue;
      // If maxInputeValue == this.maxValue and minInputValue == this.minValue, then all checkboxes should be unchecked
      if (minInputValue === this.minValue && maxInputValue === this.maxValue) {
        document.querySelectorAll(`input[name="${this.filterParamName}"]`).forEach(input => {
          input.checked = false;
        });
        this.hiddenMinInput.disabled = true;
        this.hiddenMaxInput.disabled = true;
      } else {
        this.hiddenMinInput.disabled = false;
        this.hiddenMaxInput.disabled = false;
        this.hiddenMinInput.value = minInputValue;
        this.hiddenMaxInput.value = maxInputValue;
        // Based on the selected min and max values, find the corresponding values that are beteween the min and max
        const selectedValues = this.mappedValues.filter(value => value.parsedValue >= minInputValue && value.parsedValue <= maxInputValue);
        document.querySelectorAll(`input[name="${this.filterParamName}"]`).forEach(input => {
          const inputValue = input.value
          input.checked = selectedValues.some(selectedValue => {
            return selectedValue.value === inputValue;
          });
        });
      }
    }

    /**
     * Updates the value of the 'number' type inputs.
     */
    updateNumberInputs() {
      this.minNumberInput.value = this.minSliderInput.value || this.minValue
      this.maxNumberInput.value = this.maxSliderInput.value || this.maxValue
    }

    /**
     * Updates the value of the 'range' type inputs.
     * @param {object} evt - Event object.
     */
    updateSliderInputs(evt) {
      const minValue = parseInt(this.minNumberInput.value, 10);
      const maxValue = parseInt(this.maxNumberInput.value, 10);

      if (minValue > maxValue - 1) {
        if (evt.target === this.minNumberInput) {
          this.maxSliderInput.value = minValue + 1;

          if (maxValue === this.maxValue) {
            this.minSliderInput.value = this.maxValue - 1;
          }
        } else {
          this.minSliderInput.value = maxValue - 1;
        }
      }

      if (maxValue < minValue + 1) {
        if (evt.target === this.maxNumberInput) {
          this.minSliderInput.value = maxValue - 1;

          if (minValue === this.minValue) {
            this.maxSliderInput.value = 1;
          }
        } else {
          this.maxSliderInput.value = minValue + 1;
        }
      }

      if (evt.target === this.minNumberInput) {
        this.minSliderInput.value = minValue || Number(this.minNumberInput.min);
      }

      if (evt.target === this.maxNumberInput) {
        this.maxSliderInput.value = maxValue || Number(this.maxNumberInput.max);
      }
    }
  }

  customElements.define('numeric-range', NumericRange);
}


function htmlDecode(input) {
  const doc = new DOMParser().parseFromString(input, "text/html");
  return doc.documentElement.textContent;
}